﻿import { FIQAssist } from "../app"
import { Inject, Injectable } from "@fiqas/angular-utils";
import { Utils } from "@fiqas/utils";
import { ServiceBase } from "./serviceBase"
import { WebLink, ValidationResult, HTTPMethod } from "../models/base";
import {
    IFqsResult, IIdentifiable, IIdentityLink, IIdentifiableCollectionProperty,
    IIdentifiableCollectionPropertyOf, IIdentifiableCollectionOf,
    IIdentifiablePagedCollectionOf, IIdentityResolver, IResourceLink, IResourceResolver, IResource
} from "../interfaces/iFqs"

class ActionDictionary {
    [key: string]: {
        action: <T, D>(data?: D, urlparams?: { [key: string]: any }) => ng.IPromise<T>
    }
    watch: any;
    unwatch: any;
}
ActionDictionary.prototype.watch = undefined;
ActionDictionary.prototype.unwatch = undefined;
class ActionGroup extends ActionDictionary {

}

export class FqsService extends ServiceBase {
    public static selector = "fqsService"

    constructor(
        $q: angular.IQService,
        $http: angular.IHttpService
    ) {
        super($q, $http);
        this.addResponseTransformer((d, h, s) => this.defaultResponseTransformer(d, h, s), 0);
        this.addRequestTransformers((d, h) => this.jsonRequestTransformer(d, h), 100);
        this.addHeaderSetter(this.defaultHeaderSetter);
    }
    public $onInit() {

    }

    private defaultHeaderSetter(h: ng.IHttpRequestConfigHeaders) {
        h["Accept"] = "application/fqs+json,application/json; charset=utf-8";
        return h;
    }

    private defaultResponseTransformer(data: any, headersGetter: ng.IHttpHeadersGetter, status: number) {
        var contentType = headersGetter("Content-Type") || "";
        if (data) {
            if (contentType.indexOf("application/fqs+json") > -1) {
                data = JSON.parse(data);
                if (!angular.isArray(data))
                    data = this.parseFqsResult(data);
            } else if (contentType.indexOf("application/json") > -1) {
                data = JSON.parse(data);
            } else if (contentType.indexOf("text/plain") > -1) {
                data = data;
            }
        }
        return { data: data, continue: true };
    }

    private jsonRequestTransformer(data: any, headersGetter: ng.IHttpHeadersGetter) {
        return { data: data ? JSON.stringify(data) : data, continue: true };
    }

    private handleRequest(element: any, operation: string, what: string, url: string) {
        Utils.forEachProperty<ResourceLink>(element, (p, k) => {
            element[k] = { href: p.fullHref }
        }, p => p.href !== undefined && p.hasOwnProperty("value"));
        if (element.actionLinks !== undefined)
            delete element.actionLinks;
        return element;
    }

    private handleFqsResponse(data: any, operation: string, what: string, url: string, response: any, deferred: ng.IDeferred<any>) {
        if (response.headers('Content-Type').indexOf('application/fqs+json') > -1) {
            if (data.constructor === Array) {
                return data;
            } else {
                return this.parseFqsResult(data);
            }
        }
        return data;
    }

    private parseFqsResult<R>(result: IFqsResult & R): R {
        var curies: { [id: string]: string } = {};
        (result._curies || []).forEach(c => curies[c.name] = c.href);
        var curieGetter = k => curies[k];

        var embedded: { [id: string]: IIdentifiable } = {};
        (result._embedded || []).filter(p => p.id.href !== undefined).forEach(p => {
            var href = ResourceLink.parseLink(p.id.href)[0];
            embedded[href] = p;
        });

        Utils.forEachProperty<IIdentifiable>(embedded,
            (p, k) => embedded[k] = this.promisifyIdentityProperties<IIdentifiable>(p, embedded, curieGetter),
            (p, k) => (<Object>embedded).hasOwnProperty(k));

        if (result._actions) {
            var actionLinks: { [groupkey: string]: ActionDictionary } = {};
            Utils.forEachKey(result._actions, (groups, groupkey) => {
                actionLinks[groupkey] = new ActionGroup();
                Utils.forEachKey(groups, (action, actionkey) => {
                    actionLinks[groupkey][actionkey] = {
                        action: <T, D>(data: D = null, urlparams: { [key: string]: any } = null) => this.performActionLinkRequest<D, T>(action, curieGetter, urlparams, data)
                    }
                })
            })
            result['actionLinks'] = actionLinks;
            delete result._actions;
        }
        delete result._embedded;
        delete result._curies;

        return this.promisifyIdentityProperties<R>(<R>result, embedded, curieGetter);
    }

    private performActionLinkRequest<D, T>(action: { href: string, method?: HTTPMethod }, curieGetter: (k: any) => string, urlparams: { [key: string]: any }, data: D) {
        var method = <HTTPMethod>(action.method || "GET").toUpperCase()
        var link = new IdentityLink(action.href, curieGetter, null);
        var match = RegExp(/{\?([\w,]*)}/).exec(link.fullHref);
        var keys = match && match[1] ? match[1].split(',').map(m => { return { key: m, value: null } }) : [];
        for (let k of keys) {
            k.value = urlparams ? urlparams[k.key] : undefined;
        }
        var paramstring = keys.filter(k => k.value).map(k => {
            return k.value && k.value.length !== undefined ?
                (<any[]>k.value).map(v => `${k.key}=${encodeURIComponent(v)}`).join("&") :
                `${k.key}=${encodeURIComponent(k.value)}`
        }).join("&");
        if (paramstring)
            paramstring = "?" + paramstring;
        var url = link.fullHref.replace(/{.*}/, paramstring);

        return this.performRequest<D, T>(method, url, null, data ? data : null);
    }

    private promisifyIdentityProperties<T>(item: T & Object, resources: { [id: string]: IIdentifiable }, getCurie: (key: string) => string): T {
        var handleValue = (po, k) => {
            if (po.href !== undefined) {
                if (k === "id") {
                    var res: { [id: string]: IIdentifiable } = {};
                    var href = ResourceLink.parseLink(po.href)[0];
                    res[href] = <any>item;
                }
                return this.promisifyIdentityLink(po, res || resources, getCurie);
            } else if (po.constructor !== String) {
                return this.promisifyIdentityProperties(po, resources, getCurie);
            }
            return po;
        };

        Utils.forEachProperty<IIdentityLink | IIdentityLink[]>(item, (p, k) => {
            if (p.constructor === Array) {
                item[k] = (<IIdentityLink[]>p).map(handleValue);
            } else {
                item[k] = handleValue(p, k);
            }
        }, (p, k) => k.indexOf('_') !== 0 && !!p && item.hasOwnProperty(k));
        return item;
    }

    private promisifyIdentityLink<T>(link: IIdentityLink | IResourceLink, resources: { [id: string]: IIdentifiable }, getCurie: (key: string) => string): IIdentityLink {
        var resource;
        var getter = (l: IIdentityLink) => {
            if (!resource)
                resource = this.getResource(l, resources);
            return resource;
        };
        return link['ref'] ? new ResourceLink(link.href, (<IResourceLink>link).ref, getCurie, getter) : new IdentityLink(link.href, getCurie, getter);
    }

    private getResource(link: IIdentityLink, resources: { [id: string]: IIdentifiable }): ng.IPromise<IIdentifiable> {
        var resource = resources[link.href];
        if (resource !== undefined) {
            return this.$q(resolve => resolve(resource));
        } else {
            return this.performRequest<IIdentifiable, any>("GET", link.fullHref);
        }
    }

    public createIdentityLink(link: WebLink) {
        return new IdentityLink(link.href, _ => _, (link: ResourceLink) => this.getResource(link, {}));
    }

    public GetIIdentifiableCollectionPropertyOf<T>(property: IIdentifiableCollectionPropertyOf<T>, force: boolean = false): ng.IPromise<IIdentifiableCollectionOf<T>> {
        if ((<IIdentifiableCollectionOf<T>>property).id !== undefined) {
            if (force) {
                return this.performRequest<void, IIdentifiableCollectionOf<T>>("GET", (<IIdentifiableCollectionOf<T>>property).id.fullHref);
            } else {
                return this.$q(resolve => resolve(<IIdentifiableCollectionOf<T>>property));
            }
        }
        return this.performRequest<void, IIdentifiableCollectionOf<T>>("GET", (<IIdentityResolver<IIdentifiableCollectionOf<T>>>property).fullHref);
    }

    public AddIIdentifiableCollectionPropertyOf<T>(property: IIdentifiableCollectionPropertyOf<T>, item: T, params: any): ng.IPromise<IIdentityResolver<T>> {
        var fullRef = '';
        if ((<IIdentifiableCollectionOf<T>>property).id !== undefined) {
            fullRef = (<IIdentifiableCollectionOf<T>>property).id.fullHref;
        } else {
            fullRef = (<IIdentityResolver<IIdentifiableCollectionOf<T>>>property).fullHref;
        }
        return this.performRequestWithFullResponse<T, ValidationResult>("POST", fullRef, params, item, { "Content-Type": "application/json; charset=utf-8" })
            .then(response => {
                return this.$q<WebLink>((resolve, reject) => {
                    if (response.status != 201)
                        reject(`AddIIdentifiableCollectionPropertyOf<T> Expected 201, received ${response.status}`);
                    var newId = response.headers("Location");
                    resolve(this.createIdentityLink({ href: newId }));
                });
            });

    }
}




export class IdentityLink implements IIdentityLink, IIdentityResolver<IIdentifiable> {
    constructor(
        rawvalue: string,
        protected curieGetter: (name: string) => string,
        protected objectGetter: (link: IdentityLink) => ng.IPromise<IIdentifiable>) {
        this.link = IdentityLink.parseLink(rawvalue);
    }

    public static fromLink<T extends WebLink>(source: T): IdentityLink {
        if (source['fullHref']) {
            if (source['ref'])
                return (<ResourceLink>(<any>source)).toIdentityLink();
            return (<ResourceLink>(<any>source));
        }
        return new IdentityLink(source.href, _ => _, _ => null);
    }

    public equals(link: WebLink) {
        return (link['fullHref'] || link.href) == this.fullHref;
    }

    private link: [string, string] = ["", ""];

    public static parseLink(value: string) {
        var result: [string, string] = ["", ""];
        if (value[0] === '[' && value[value.length - 1] === ']') {
            var l = value.substr(1, value.length - 2).split(':').reverse();
            result[0] = l[0];
            result[1] = l[1];
        } else {
            result[0] = value;
        }
        return result;
    }
    public get href(): string {
        return this.link[0]
    }
    public set href(value: string) {
        this.link = IdentityLink.parseLink(value);
    }
    public get fullHref() {
        return this.curieGetter(this.link[1]) + this.link[0];
    }

    public toString() {
        return this.fullHref;
    }

    public get base() {
        return this.link[1];
    }
    public get value() {
        return this.objectGetter(this);
    }
    public toJSON(): string | WebLink {
        return { href: this.fullHref };
    }
}

export class ResourceLink extends IdentityLink implements IResourceResolver<IResource> {
    constructor(
        private rawvalue: string,
        ref: string,
        curieGetter: (name: string) => string,
        resourceGetter: (link: ResourceLink) => ng.IPromise<IIdentifiable>) {
        super(rawvalue, curieGetter, resourceGetter);
        this._ref = ref;
    }
    public _ref: string;

    public get ref() {
        return this._ref;
    }
    public toJSON() {
        return this._ref;
    }
    public toIdentityLink() {
        return new IdentityLink(this.rawvalue, this.curieGetter, this.objectGetter);
    }
}
